/**
 * src/pages/Reports.tsx
 * 管理者用 日報一覧ページ
 *
 * 旧: Personnel.tsx の「日報一覧」タブをそのまま移設したもの。
 * 日報API・工程進捗反映APIは変更していない。
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { api } from '../api/client'
import { ReportDetailModal } from '../components/ReportDetailModal'
import { ReportFormModal }   from '../components/ReportFormModal'
import { ReportProgressReflectionPanel } from '../components/ReportProgressReflectionPanel'
import { UndoToast } from '../components/ConfirmDialog'
import {
  STAGE_LABELS, KAIKAI_KIKI_LABEL, isKaikaiKiki,
  parseWorkDetails, parseImageUrls, totalPages,
} from '../components/reportUtils'
import { thisMonthJst } from '../utils/jst'

type SortState = { key: string; dir: 'asc' | 'desc' }

function SortTh({ label, colKey, sort, onSort, style }: {
  label: string
  colKey: string
  sort: SortState
  onSort: (key: string) => void
  style?: CSSProperties
}) {
  const active = sort.key === colKey
  return (
    <th
      onClick={() => onSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.2, fontSize: 10 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  )
}

function monthRange(ym: string): { start_date: string; end_date: string } {
  const [y, m] = ym.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return { start_date: `${ym}-01`, end_date: `${ym}-${String(lastDay).padStart(2, '0')}` }
}

export default function Reports() {
  const [month, setMonth] = useState<string>(thisMonthJst())
  const [reports, setReports]       = useState<any[]>([])
  const [works, setWorks]           = useState<{ id: string; title: string }[]>([])
  const [assistants, setAssistants] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading]       = useState(true)
  const [selectedReport, setSelectedReport] = useState<any | null>(null)
  const [showReportForm, setShowReportForm] = useState(false)
  const [reportSort, setReportSort] = useState<SortState>({ key: 'submitted_at', dir: 'desc' })
  const [undoState, setUndoState]   = useState<{ id: string; label: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { start_date, end_date } = monthRange(month)
    const [rpts, ws, asts] = await Promise.all([
      api.reports.list({ start_date, end_date }),
      api.works.list().catch(() => []),
      api.assistants.list(),
    ])
    setReports(rpts)
    setWorks(ws)
    setAssistants(asts)
    setLoading(false)
  }, [month])

  useEffect(() => { load() }, [load])

  const toggleReportSort = useCallback((key: string) => {
    setReportSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  const sortedReports = useMemo(() => {
    const list = [...reports]
    const { key, dir } = reportSort
    list.sort((a: any, b: any) => {
      let av: any
      let bv: any
      if (key === 'pages_done') {
        const wA = parseWorkDetails(a.work_details)
        const wB = parseWorkDetails(b.work_details)
        av = wA ? totalPages(wA) : (a.pages_done ?? 0)
        bv = wB ? totalPages(wB) : (b.pages_done ?? 0)
      } else if (key === 'work_title') {
        const wA = parseWorkDetails(a.work_details)
        const wB = parseWorkDetails(b.work_details)
        av = wA?.[0]?.work_title ?? a.work_title ?? ''
        bv = wB?.[0]?.work_title ?? b.work_title ?? ''
      } else if (key === 'episode_number') {
        const wA = parseWorkDetails(a.work_details)
        const wB = parseWorkDetails(b.work_details)
        av = Number(wA?.[0]?.episode_number ?? a.episode_number ?? 0)
        bv = Number(wB?.[0]?.episode_number ?? b.episode_number ?? 0)
      } else if (key === 'stage') {
        const wA = parseWorkDetails(a.work_details)
        const wB = parseWorkDetails(b.work_details)
        av = wA?.[0]?.stage ?? a.stage ?? ''
        bv = wB?.[0]?.stage ?? b.stage ?? ''
      } else {
        av = a[key] ?? ''
        bv = b[key] ?? ''
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      }
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return list
  }, [reports, reportSort])

  if (loading) return <div className="loading">LOADING...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
      </div>

      {/* 未反映の工程進捗候補パネル */}
      <ReportProgressReflectionPanel
        onOpenReport={(dailyReportId) => {
          const r = reports.find((rep: any) => rep.id === dailyReportId)
          if (r) setSelectedReport(r)
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowReportForm(true)}>
          + 日報入力
        </button>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortTh label="日付" colKey="report_date" sort={reportSort} onSort={toggleReportSort} />
                <SortTh label="担当者" colKey="assistant_name" sort={reportSort} onSort={toggleReportSort} />
                <SortTh label="作品名" colKey="work_title" sort={reportSort} onSort={toggleReportSort} />
                <SortTh label="話数" colKey="episode_number" sort={reportSort} onSort={toggleReportSort} />
                <SortTh label="工程" colKey="stage" sort={reportSort} onSort={toggleReportSort} />
                <SortTh label="合計P" colKey="pages_done" sort={reportSort} onSort={toggleReportSort} />
                <th>引継ぎ</th>
                <th>画像</th>
                <SortTh label="提出日時" colKey="submitted_at" sort={reportSort} onSort={toggleReportSort} />
              </tr>
            </thead>
            <tbody>
              {sortedReports.length === 0 && (
                <tr><td colSpan={9} className="text-muted" style={{ textAlign: 'center', padding: 32 }}>日報なし</td></tr>
              )}
              {sortedReports.map((r: any) => {
                const wds      = parseWorkDetails(r.work_details)
                const pages    = wds ? totalPages(wds) : (r.pages_done ?? 0)
                const imgCount = parseImageUrls(r.image_urls).length
                const firstWd  = wds?.[0]
                const workTitle = firstWd
                  ? (isKaikaiKiki(firstWd.work_id, firstWd.work_title) ? KAIKAI_KIKI_LABEL : (firstWd.work_title ?? '—'))
                  : (r.work_title ?? '—')
                const episodeNum = firstWd
                  ? (firstWd.episode_number != null ? `#${firstWd.episode_number}` : '—')
                  : (r.episode_number != null ? `#${r.episode_number}` : '—')
                const stageLabel = firstWd
                  ? (STAGE_LABELS[firstWd.stage ?? ''] ?? firstWd.stage ?? '—')
                  : (STAGE_LABELS[r.stage ?? ''] ?? r.stage ?? '—')
                return (
                  <tr key={r.id}
                    onClick={() => setSelectedReport(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono">{r.report_date}</td>
                    <td style={{ fontWeight: 500 }}>
                      {r.assistant_name}
                      {r.is_help && <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 4 }}>ヘルプ</span>}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {workTitle}
                      {wds && wds.length > 1 && (
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}>他{wds.length - 1}件</span>
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{episodeNum}</td>
                    <td style={{ fontSize: 12 }}>{stageLabel}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>
                      {pages > 0 ? `${pages}P` : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 160,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.handover_notes
                        ? (r.handover_notes === '特になし'
                            ? <span style={{ opacity: 0.4 }}>特になし</span>
                            : r.handover_notes)
                        : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: imgCount > 0 ? 'var(--blue)' : 'var(--ink-3)' }}>
                      {imgCount > 0 ? `📷 ${imgCount}枚` : '—'}
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                      {r.submitted_at ? r.submitted_at.slice(0, 16).replace('T', ' ') : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showReportForm && (
        <ReportFormModal
          mode="admin"
          assistants={assistants}
          works={works}
          onClose={() => setShowReportForm(false)}
          onSaved={(id, name, isUpdate) => {
            setShowReportForm(false)
            load()
            if (isUpdate) {
              setUndoState({ id, label: `${name} の日報（上書き）` })
            } else if (id) {
              setUndoState({ id, label: `${name} の日報` })
            }
          }}
        />
      )}
      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          isAdmin={true}
        />
      )}
      {undoState && (
        <UndoToast
          msg={`${undoState.label}を追加しました`}
          onUndo={async () => { await api.reports.delete(undoState.id); await load() }}
          onClose={() => setUndoState(null)}
        />
      )}
    </div>
  )
}
